/**
 * Orden de presentación de permisos DENTRO de una categoría (UX, no del backend).
 *
 * El catálogo del backend entrega los permisos de cada categoría en orden arbitrario, lo que hace
 * difícil encontrarlos al crear/editar un rol o el catálogo de un tipo de tenant. Acá derivamos de
 * la `key` un orden estable y predecible: primero agrupamos por RECURSO (alfabético) y dentro de
 * cada recurso ordenamos por VERBO con un ranking canónico ("crear → ver → editar → estados →
 * gestión → eliminar"), dejando `delete` SIEMPRE al final por ser la acción destructiva.
 *
 * Una key tiene forma `<dominio>.<...recurso>.<verbo(s)>`, p.ej. `tenant.user.create`,
 * `retail.user.create.lateral`, `tenant.user.password.reset`, `mdm.device.status.view`. El verbo
 * puede ser compuesto (2 segmentos); lo detectamos desde el final contra una lista conocida y, si
 * no matchea, el verbo es el último segmento. El recurso es el segmento inmediatamente anterior.
 *
 * Este helper es COMPARTIDO entre el PermissionPicker (crear/editar rol) y el CatalogEditor
 * (catálogo de tipo de tenant) para no duplicar el criterio de orden.
 */

/** Verbos compuestos (2 segmentos finales) que deben tratarse como UN verbo. */
const COMPOUND_VERBS = new Set([
    'create.lateral',
    'password.reset',
    'password_reset',
    'mfa.reset',
    'mfa_reset',
    'status.view',
    'lock.manual',
]);

/**
 * Ranking canónico del verbo (menor = primero). Lo no mapeado cae en `OTHER_RANK` (antes de delete,
 * desempatado alfabéticamente), y `delete` SIEMPRE queda último.
 */
const VERB_RANK: Record<string, number> = {
    create: 0,
    'create.lateral': 1,
    list: 2,
    read: 3,
    view: 4,
    'status.view': 4,
    update: 5,
    status: 6,
    enable: 6,
    disable: 6,
    assign: 7,
    revoke: 7,
    manage: 8,
    'password.reset': 9,
    password_reset: 9,
    'mfa.reset': 10,
    mfa_reset: 10,
    // 11 reservado para verbos no mapeados (OTHER_RANK)
    delete: 12,
};

/** Rank de los verbos no mapeados: después de todo lo conocido, antes de delete. */
const OTHER_RANK = 11;
const DELETE_RANK = 12;

/**
 * Sub-grupo de ACCIÓN dentro de una categoría (UX). Cada permiso cae en exactamente uno según su
 * verbo; los verbos no mapeados caen en "Gestión" (el catch-all, justo antes de "Eliminar").
 * El `rank` define el orden de los sub-encabezados; `id` es estable (para keys de React).
 */
export interface ActionGroup {
    id: string;
    label: string;
    rank: number;
}

const GROUP_CREAR: ActionGroup = { id: 'crear', label: 'Crear', rank: 0 };
const GROUP_VER: ActionGroup = { id: 'ver', label: 'Ver', rank: 1 };
const GROUP_EDITAR: ActionGroup = { id: 'editar', label: 'Editar', rank: 2 };
const GROUP_ESTADO: ActionGroup = { id: 'estado', label: 'Estado', rank: 3 };
const GROUP_ASIGNACION: ActionGroup = { id: 'asignacion', label: 'Asignación', rank: 4 };
const GROUP_GESTION: ActionGroup = { id: 'gestion', label: 'Gestión', rank: 5 };
const GROUP_ELIMINAR: ActionGroup = { id: 'eliminar', label: 'Eliminar', rank: 6 };

/**
 * Mapeo verbo → sub-grupo de acción. Lo NO mapeado (apply, cancel, lock.manual, mdm_config, …) cae
 * en "Gestión" vía `actionGroupOf`. `delete` → "Eliminar"; el resto según esta tabla.
 */
const VERB_GROUP: Record<string, ActionGroup> = {
    create: GROUP_CREAR,
    'create.lateral': GROUP_CREAR,
    list: GROUP_VER,
    read: GROUP_VER,
    view: GROUP_VER,
    'status.view': GROUP_VER,
    update: GROUP_EDITAR,
    status: GROUP_ESTADO,
    enable: GROUP_ESTADO,
    disable: GROUP_ESTADO,
    assign: GROUP_ASIGNACION,
    revoke: GROUP_ASIGNACION,
    manage: GROUP_GESTION,
    'password.reset': GROUP_GESTION,
    password_reset: GROUP_GESTION,
    'mfa.reset': GROUP_GESTION,
    mfa_reset: GROUP_GESTION,
    delete: GROUP_ELIMINAR,
};

/**
 * Sub-grupo de acción al que pertenece una key de permiso. Deriva el verbo con `parsePermissionKey`
 * y lo mapea contra `VERB_GROUP`; cualquier verbo no mapeado cae en "Gestión" (el catch-all).
 */
export function actionGroupOf(key: string): ActionGroup {
    const { verb } = parsePermissionKey(key);
    return VERB_GROUP[verb] ?? GROUP_GESTION;
}

/**
 * Agrupa (sin mutar) una lista de items con `key` por sub-grupo de ACCIÓN, devolviendo SOLO los
 * grupos no vacíos, en el orden canónico de `ActionGroup.rank`. Dentro de cada grupo, los items
 * quedan ordenados por recurso → verbo (igual que `sortByResourceAndAction`).
 *
 * Compartido entre el PermissionPicker y el CatalogEditor para no duplicar el criterio de
 * sub-agrupamiento.
 */
export function groupByAction<T extends { key: string }>(
    items: readonly T[],
): { group: ActionGroup; items: T[] }[] {
    const buckets = new Map<string, { group: ActionGroup; items: T[] }>();
    for (const item of items) {
        const group = actionGroupOf(item.key);
        let bucket = buckets.get(group.id);
        if (!bucket) {
            bucket = { group, items: [] };
            buckets.set(group.id, bucket);
        }
        bucket.items.push(item);
    }
    return [...buckets.values()]
        .sort((a, b) => a.group.rank - b.group.rank)
        .map((b) => ({ group: b.group, items: sortByResourceAndAction(b.items) }));
}

interface ParsedPermission {
    /** Segmento de recurso (el inmediatamente anterior al verbo). '' si no se pudo derivar. */
    resource: string;
    /** Verbo (último segmento, o sufijo compuesto conocido). */
    verb: string;
}

/** Deriva `{ resource, verb }` de una key de permiso. */
export function parsePermissionKey(key: string): ParsedPermission {
    const segments = key.split('.');
    if (segments.length <= 1) {
        return { resource: '', verb: key };
    }

    // ¿Los dos últimos segmentos forman un verbo compuesto conocido?
    const lastTwo = segments.slice(-2).join('.');
    if (segments.length >= 3 && COMPOUND_VERBS.has(lastTwo)) {
        return { resource: segments[segments.length - 3], verb: lastTwo };
    }

    // Verbo simple = último segmento; recurso = el anterior.
    return {
        resource: segments[segments.length - 2],
        verb: segments[segments.length - 1],
    };
}

/** Rank del verbo: conocido → su rank; delete → último; resto → OTHER_RANK. */
function verbRank(verb: string): number {
    if (verb === 'delete') return DELETE_RANK;
    return VERB_RANK[verb] ?? OTHER_RANK;
}

/**
 * Compara dos keys de permiso para ordenarlas dentro de una categoría:
 * 1) por recurso (alfabético) → agrupa juntos los permisos del mismo recurso;
 * 2) por rank de verbo (crear → ver → editar → estados → gestión → eliminar);
 * 3) desempate alfabético por verbo (relevante entre verbos no mapeados con el mismo rank);
 * 4) desempate final por key completa (estable y determinístico).
 */
export function comparePermissions(aKey: string, bKey: string): number {
    const a = parsePermissionKey(aKey);
    const b = parsePermissionKey(bKey);

    if (a.resource !== b.resource) {
        return a.resource < b.resource ? -1 : 1;
    }

    const ra = verbRank(a.verb);
    const rb = verbRank(b.verb);
    if (ra !== rb) return ra - rb;

    if (a.verb !== b.verb) return a.verb < b.verb ? -1 : 1;

    return aKey < bKey ? -1 : aKey > bKey ? 1 : 0;
}

/**
 * Ordena (sin mutar) una lista de items que exponen una `key`, aplicando `comparePermissions`.
 * Usado por el PermissionPicker y el CatalogEditor dentro de cada grupo de categoría.
 */
export function sortByResourceAndAction<T extends { key: string }>(items: readonly T[]): T[] {
    return [...items].sort((a, b) => comparePermissions(a.key, b.key));
}
